import React, { useEffect } from 'react';
import {
    Modal,
    TextInput,
    Switch,
    Button,
    Stack,
    Flex,
    Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useMutation } from '@apollo/client';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import {
    CREATE_TAX_CODE_CONFIG,
    UPDATE_TAX_CODE_CONFIG,
} from '../../apollo/mutations/vatConfig';
import { GET_TAX_CODE_CONFIGS } from '../../apollo/queries/vatConfig';
import { TaxCodeConfigRow } from './vatConfigTypes';

type Props = {
    opened: boolean;
    onClose: () => void;
    taxCodeConfig?: TaxCodeConfigRow;
};

type FormValues = {
    code: string;
    description: string;
    isZeroRated: boolean;
};

export function TaxCodeConfigFormModal({
    opened,
    onClose,
    taxCodeConfig,
}: Props) {
    const form = useForm<FormValues>({
        initialValues: {
            code: taxCodeConfig?.code ?? '',
            description: taxCodeConfig?.description ?? '',
            isZeroRated: taxCodeConfig?.isZeroRated ?? false,
        },
        validate: {
            code: value =>
                value.trim().length === 0 ? 'Enter a SAP tax code' : null,
            description: value =>
                value.trim().length === 0 ? 'Enter a description' : null,
        },
    });

    useEffect(() => {
        if (opened) {
            form.setValues({
                code: taxCodeConfig?.code ?? '',
                description: taxCodeConfig?.description ?? '',
                isZeroRated: taxCodeConfig?.isZeroRated ?? false,
            });
            form.clearErrors();
            form.resetDirty();
        }
    }, [taxCodeConfig, opened]);

    const [createTaxCodeConfig, { loading: creating, error: createError }] =
        useMutation(CREATE_TAX_CODE_CONFIG, {
            refetchQueries: [{ query: GET_TAX_CODE_CONFIGS }],
        });

    const [updateTaxCodeConfig, { loading: updating, error: updateError }] =
        useMutation(UPDATE_TAX_CODE_CONFIG, {
            refetchQueries: [{ query: GET_TAX_CODE_CONFIGS }],
        });

    const handleClose = () => {
        form.reset();
        onClose();
    };

    const handleSubmit = async (values: FormValues) => {
        const input = {
            code: values.code.trim().toUpperCase(),
            description: values.description.trim(),
            isZeroRated: values.isZeroRated,
        };

        if (taxCodeConfig) {
            const result = await updateTaxCodeConfig({
                variables: { id: taxCodeConfig.id, input },
            }).catch(() => null);
            if (!result?.data) {
                return;
            }
            notifications.show({
                title: 'Tax code updated',
                message: `${input.code} has been updated.`,
                color: 'green',
            });
        } else {
            const result = await createTaxCodeConfig({
                variables: { input },
            }).catch(() => null);
            if (!result?.data) {
                return;
            }
            notifications.show({
                title: 'Tax code added',
                message: `${input.code} has been added.`,
                color: 'green',
            });
        }

        handleClose();
    };

    const saving = creating || updating;
    const mutationError = createError ?? updateError;

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={taxCodeConfig ? 'Edit Tax Code' : 'Add Tax Code'}
            size="md"
            centered
        >
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="md">
                    <TextInput
                        label="SAP Tax Code"
                        description="SAP MWSKZ code, e.g. S0, S2"
                        placeholder="S0"
                        maxLength={10}
                        required
                        {...form.getInputProps('code')}
                        onChange={event =>
                            form.setFieldValue(
                                'code',
                                event.currentTarget.value.toUpperCase(),
                            )
                        }
                    />
                    <TextInput
                        label="Description"
                        placeholder="Zero-rated"
                        required
                        {...form.getInputProps('description')}
                    />
                    <Switch
                        label="Zero-rated"
                        description="When enabled, products with this tax code are treated as VAT-free regardless of the store's country"
                        {...form.getInputProps('isZeroRated', {
                            type: 'checkbox',
                        })}
                    />
                    {mutationError && (
                        <Alert
                            color="red"
                            icon={<IconAlertTriangle size={16} />}
                            title="Error saving tax code"
                        >
                            {mutationError.message}
                        </Alert>
                    )}
                    <Flex justify="flex-end" gap="xs">
                        <Button
                            type="button"
                            variant="outline"
                            fullWidth={false}
                            onClick={handleClose}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            fullWidth={false}
                            loading={saving}
                        >
                            {taxCodeConfig ? 'Update' : 'Add'}
                        </Button>
                    </Flex>
                </Stack>
            </form>
        </Modal>
    );
}
